import { useEffect, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { VesselNominationOutletContext } from "@features/vessel-nomination/add/constants";
import { FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import type { VesselNominationFormData } from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { LANG_BASE } from "../PreArrivalQuestionnaire";

const TechnicalDetails = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { trigger, watch } = formData;
  const loa = watch(FORM_FIELDS.LOA);
  const maxTrim = watch(FORM_FIELDS.MAX_TRIM);
  const previousValuesRef = useRef({ loa, maxTrim });

  useEffect(() => {
    const previousValues = previousValuesRef.current;

    if (loa !== previousValues.loa && maxTrim) {
      void trigger(FORM_FIELDS.MAX_TRIM);
    }

    previousValuesRef.current = { loa, maxTrim };
  }, [loa, maxTrim, trigger]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${LANG_BASE}.sections.technical-details`)}
        className="grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"
      >
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.LOA>
          testId="nomination-loa-input"
          name={FORM_FIELDS.LOA}
          type="number"
          label={t(`${LANG_BASE}.fields.loa`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          disabled
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.BALLAST>
          testId="nomination-ballast-input"
          name={FORM_FIELDS.BALLAST}
          type="number"
          label={t(`${LANG_BASE}.fields.ballast`)}
          onFieldChange={updateFormData}
          maxDigits={12}
          decimals={3}
          adornment="MT"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.DRAUGHT_SUMMER>
          testId="nomination-draught-summer-input"
          name={FORM_FIELDS.DRAUGHT_SUMMER}
          type="number"
          label={t(`${LANG_BASE}.fields.draught-summer`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
        <FormItem<VesselNominationFormData, typeof FORM_FIELDS.DRAUGHT_TROPICAL>
          testId="nomination-draught-tropical-input"
          name={FORM_FIELDS.DRAUGHT_TROPICAL}
          type="number"
          label={t(`${LANG_BASE}.fields.draught-tropical`)}
          onFieldChange={updateFormData}
          maxDigits={8}
          decimals={3}
          adornment="m"
          thousandSeparator
          canBeginWithZero={false}
        />
      </FormSection>
    </FormProvider>
  );
};

export default TechnicalDetails;
